import {useCallback,useEffect,useRef,useState} from 'react';
import api,{downloadFile,getErrorMessage} from '@/lib/api';

const LABELS={QUEUED:'Antre',GENERATING:'Menyusun',VALIDATING:'Validasi',READY:'Siap',FAILED:'Gagal',EXPIRED:'Kedaluwarsa'};
// Pack kinds are an explicit server allowlist; scoped kinds need one account, audit area or procedure.
const KINDS=[['EWP_ENGAGEMENT','Semua KKA terbaru'],['ACCOUNT','Paket akun'],['AREA','Paket area audit'],['PROCEDURE','Paket prosedur'],
 ['INDEX_A','Indeks A — Perencanaan'],['INDEX_B','Indeks B — Pekerjaan lapangan'],['INDEX_C','Indeks C — Penyelesaian'],
 ['INDEX_D','Indeks D — Pelaporan'],['INDEX_E','Indeks E — Mutu dan arsip'],['FULL_ENGAGEMENT','Seluruh perikatan (A–E terpisah)']];
const KIND_LABEL=Object.fromEntries(KINDS);

function newKey(){
 const random=globalThis.crypto?.randomUUID?globalThis.crypto.randomUUID():`${Date.now()}-${Math.random().toString(36).slice(2)}`;
 return `pack-${random}`.replace(/[^A-Za-z0-9_-]/g,'').slice(0,100);
}

export default function WordPackJobs({id}){
 const [rows,setRows]=useState([]),[busy,setBusy]=useState(false),[error,setError]=useState('');
 const [kind,setKind]=useState('EWP_ENGAGEMENT'),[scope,setScope]=useState(''),[options,setOptions]=useState([]);
 const request=useRef(0);
 const load=useCallback(async()=>{
  const ticket=++request.current;setError('');
  try{const {data}=await api.get(`/engagements/${id}/word-packs`);if(ticket===request.current)setRows(data.items||[]);}
  catch(e){if(ticket===request.current){setRows([]);setError(getErrorMessage(e));}}
 },[id]);
 useEffect(()=>{load();return()=>{request.current+=1;};},[load]);
 const act=async(fn)=>{
  // Always reload after an attempt: a failed download may have expired the pack server-side,
  // so a stale READY row must never remain actionable.
  setBusy(true);setError('');let failure='';
  try{await fn();}catch(e){failure=getErrorMessage(e);}
  try{await load();}finally{if(failure)setError(failure);setBusy(false);}
 };
 const scoped=['ACCOUNT','AREA','PROCEDURE'].includes(kind);
 const choose=async(value)=>{
  setKind(value);setScope('');setOptions([]);
  if(!['ACCOUNT','AREA','PROCEDURE'].includes(value))return;
  try{
   if(value==='ACCOUNT'){const {data}=await api.get(`/engagements/${id}/accounts`);setOptions((data.accounts||[]).map(a=>[a.id,`${a.account_code||''} ${a.account_name||a.id}`.trim()]));}
   if(value==='AREA'){const {data}=await api.get('/meta/audit-areas');setOptions((data.areas||[]).map(a=>[a,a]));}
   if(value==='PROCEDURE'){
    const {data}=await api.get(`/engagements/${id}/ewps`);const seen=new Map();
    for(const w of data||[])if(w.procedure_id&&!seen.has(w.procedure_id))seen.set(w.procedure_id,`${w.wp_ref||''} ${w.wp_title||w.procedure_id}`.trim());
    setOptions([...seen.entries()]);
   }
  }catch(e){setError(getErrorMessage(e));}
 };
 const create=()=>act(()=>api.post(`/engagements/${id}/word-packs`,{kind,idempotency_key:newKey(),...(scoped?{scope}:{})}));
 const retry=(row)=>act(()=>api.post(`/word-packs/${row.id}/retry`,{expected_version:row.version}));
 const download=(row)=>act(()=>downloadFile(`/word-packs/${row.id}/download`,row.filename||'pack.zip'));
 return <section className="bg-white border border-border rounded-sm p-3 space-y-2 text-xs" aria-label="Paket Word latar belakang" data-testid="word-packs">
  <div className="flex flex-wrap items-center gap-2">
   <span className="font-head font-semibold uppercase text-muted-foreground">Paket KKA Word (latar belakang)</span>
   <label>Jenis paket<select aria-label="Jenis paket Word" value={kind} onChange={e=>choose(e.target.value)} className="block border p-2">
    {KINDS.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></label>
   {scoped&&<label>Lingkup<select aria-label="Lingkup paket Word" value={scope} onChange={e=>setScope(e.target.value)} className="block border p-2 max-w-full">
    <option value="">Pilih lingkup</option>{options.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></label>}
   <button type="button" disabled={busy||(scoped&&!scope)} onClick={create} className="border rounded p-2">Buat paket KKA</button>
   <button type="button" disabled={busy} onClick={load} className="border rounded p-2">Perbarui status</button>
  </div>
  <p>Paket disusun oleh worker terpisah dari salinan kerja setiap kertas kerja dalam lingkup, dengan daftar isi yang menautkan setiap berkas. Bagian tanpa sumber terkini dicantumkan beserta alasannya, bukan dibuat-buat. Unduhan memerlukan akses dan sumber yang masih sama; paket kedaluwarsa setelah 24 jam.</p>
  {error&&<p role="alert">{error}</p>}
  {!rows.length?<p>Belum ada paket.</p>:<div className="overflow-x-auto"><table className="w-full text-left">
   <thead><tr>{['Dibuat','Jenis / lingkup','Status','Dokumen','Percobaan','Kedaluwarsa','Kode','Berkas / SHA-256'].map(h=><th key={h} className="p-2">{h}</th>)}</tr></thead>
   <tbody>{rows.map(row=><tr key={row.id}>
    <td className="p-2">{row.created_at}</td><td className="p-2">{KIND_LABEL[row.kind]||row.kind||'—'}{row.scope?` · ${row.scope}`:''}</td><td className="p-2">{LABELS[row.status]||row.status}</td>
    <td className="p-2">{row.item_count??'—'}</td><td className="p-2">{row.attempts}</td><td className="p-2">{row.expires_at||'—'}</td>
    <td className="p-2">{row.error_code||'—'}</td>
    <td className="p-2">
     {row.status==='READY'&&<button type="button" disabled={busy} onClick={()=>download(row)} className="underline">{row.filename}</button>}
     {row.status==='FAILED'&&<button type="button" disabled={busy} onClick={()=>retry(row)} className="border rounded p-1">Coba lagi</button>}
     {row.sha256&&<div className="break-all font-mono">{row.sha256}</div>}
    </td></tr>)}</tbody></table></div>}
 </section>;
}
